import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  Bell,
  BriefcaseBusiness,
  FileText,
  Globe2,
  LayoutDashboard,
  Palette,
  Puzzle,
  Receipt,
  Rocket,
  Search,
  Settings,
  Settings2,
  UserRound,
  Video,
} from "lucide-react";
import { CMS_MODULES } from "@/modules/cms/constants/modules";
import { Modal } from "@/components/ui/Modal";

const NAV_TARGETS = [
  { id: "dashboard", label: "Go to Dashboard", to: "/", icon: LayoutDashboard },
  {
    id: "bd",
    label: "Business Development",
    to: "/business-development",
    icon: BriefcaseBusiness,
  },
  {
    id: "bd-inquiries",
    label: "BD · Inquiries",
    to: "/business-development/inquiries",
    icon: BriefcaseBusiness,
  },
  {
    id: "bd-applications",
    label: "BD · Applications",
    to: "/business-development/applications",
    icon: BriefcaseBusiness,
  },
  {
    id: "bd-announcements",
    label: "BD · Announcements",
    to: "/business-development/announcements",
    icon: BriefcaseBusiness,
  },
  {
    id: "bd-crm",
    label: "BD · CRM",
    to: "/business-development/crm",
    icon: BriefcaseBusiness,
  },
  {
    id: "sales-engine",
    label: "Sales Engine",
    to: "/sales-engine",
    icon: Rocket,
  },
  {
    id: "se-audience",
    label: "Sales Engine · Audience",
    to: "/sales-engine/audience",
    icon: Rocket,
  },
  {
    id: "se-campaigns",
    label: "Sales Engine · Campaigns",
    to: "/sales-engine/campaigns",
    icon: Rocket,
  },
  {
    id: "se-successful",
    label: "Sales Engine · Successful Leads",
    to: "/sales-engine/successful-leads",
    icon: Rocket,
  },
  {
    id: "se-dead",
    label: "Sales Engine · Dead Leads",
    to: "/sales-engine/dead-leads",
    icon: Rocket,
  },
  {
    id: "se-analytics",
    label: "Sales Engine · Analytics",
    to: "/sales-engine/analytics",
    icon: Rocket,
  },
  {
    id: "meetings",
    label: "Meetings Hub",
    to: "/meetings",
    icon: Video,
  },
  {
    id: "meetings-calendar",
    label: "Meetings · Calendar",
    to: "/meetings/calendar",
    icon: Video,
  },
  {
    id: "meetings-upcoming",
    label: "Meetings · Upcoming",
    to: "/meetings/upcoming",
    icon: Video,
  },
  {
    id: "meetings-analytics",
    label: "Meetings · Analytics",
    to: "/meetings/analytics",
    icon: Video,
  },
  {
    id: "bd-tasks",
    label: "BD · Tasks",
    to: "/business-development/tasks",
    icon: BriefcaseBusiness,
  },
  {
    id: "bd-documents",
    label: "BD · Sales Documents",
    to: "/business-development/documents",
    icon: FileText,
  },
  {
    id: "bd-reports",
    label: "BD · Reports",
    to: "/business-development/reports",
    icon: BarChart3,
  },
  { id: "finance", label: "Finance Dashboard", to: "/finance", icon: Receipt },
  { id: "finance-invoices", label: "Finance · Invoices", to: "/finance/invoices", icon: FileText },
  { id: "cms", label: "Website CMS", to: "/cms", icon: Globe2 },
  ...CMS_MODULES.map((mod) => ({
    id: `cms-${mod.id}`,
    label: `CMS · ${mod.label}`,
    to: mod.to,
    icon: mod.icon,
  })),
  { id: "growth-strategy", label: "Growth Strategy Center", to: "/growth-strategy", icon: BarChart3 },
  { id: "integrations", label: "Integration Hub", to: "/integrations", icon: Puzzle },
  {
    id: "system-settings",
    label: "System Settings",
    to: "/system-settings",
    icon: Settings,
  },
  { id: "profile", label: "My Profile", to: "/profile", icon: UserRound },
  { id: "user-settings", label: "User Settings", to: "/user-settings", icon: Settings2 },
  { id: "appearance", label: "Appearance", to: "/appearance", icon: Palette },
  {
    id: "notification-preferences",
    label: "Notification Preferences",
    to: "/notification-preferences",
    icon: Bell,
  },
  { id: "invoices", label: "Jump to invoices", to: "/finance/invoices", icon: FileText },
];

const RECENT_KEY = "opera-recent-searches";

export function GlobalSearch({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    try {
      setRecent(JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"));
    } catch {
      setRecent([]);
    }
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return NAV_TARGETS;
    return NAV_TARGETS.filter((item) => item.label.toLowerCase().includes(q));
  }, [query]);

  const go = (to: string, label: string) => {
    const next = [label, ...recent.filter((r) => r !== label)].slice(0, 5);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    navigate(to);
    onClose();
  };

  return (
    <Modal open={open} title="Command palette" onClose={onClose}>
      <div className="search-palette" style={{ boxShadow: "none", border: 0 }}>
        <div className="search-palette__header">
          <Search size={16} aria-hidden />
          <input
            className="search-palette__input"
            autoFocus
            placeholder="Search modules, actions, pages…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search"
          />
          <kbd className="search-palette__kbd">Esc</kbd>
        </div>
        {!query && recent.length ? (
          <div className="search-palette__section">
            <div className="search-palette__label">Recent searches</div>
            {recent.map((item) => (
              <button
                key={item}
                type="button"
                className="search-palette__item"
                onClick={() => {
                  const match = NAV_TARGETS.find((n) => n.label === item);
                  if (match) go(match.to, match.label);
                }}
              >
                {item}
              </button>
            ))}
          </div>
        ) : null}
        <div className="search-palette__section">
          <div className="search-palette__label">Quick navigation</div>
          {results.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                className="search-palette__item"
                onClick={() => go(item.to, item.label)}
              >
                <Icon size={16} />
                <span>{item.label}</span>
                <kbd className="search-palette__kbd" style={{ marginLeft: "auto" }}>
                  ↵
                </kbd>
              </button>
            );
          })}
          {!results.length ? (
            <div style={{ padding: "0.75rem", color: "var(--text-muted)", fontSize: "0.85rem" }}>
              No matches. Command palette backend hooks can plug in later.
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
